import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type UtmKey = (typeof UTM_KEYS)[number];

// Mirror the email function's caps (`schemas.ts`); an over-long value drops
// the record there, it never fails the lead.
const LINE_MAX = 200;
const URL_PATH_MAX = 2048;

/**
 * Where the visit that produced a lead came from. First-party and cookieless:
 * the entry URL and the referrer the browser hands over anyway, read once at
 * boot and sent with every form post by `EmailService`. GA4 and Ads see only
 * the consented share of visits; this reaches the notification email for
 * every lead.
 */
export type LeadSource = {
  /** The entry path with its `utm_*` query only; other parameters stay out. */
  landingPage: string;
  /** `document.referrer` at entry, empty on a direct visit. */
  referrer: string;
  utm: Partial<Record<UtmKey, string>>;
};

export function readLeadSource(href: string, referrer: string): LeadSource {
  const url = new URL(href);
  const utm = Object.fromEntries(
    UTM_KEYS.flatMap(key => {
      const value = url.searchParams.get(key)?.trim().slice(0, LINE_MAX);
      return value ? [[key, value]] : [];
    }),
  ) as LeadSource['utm'];
  const query = new URLSearchParams(utm).toString();
  const landingPage = query ? `${url.pathname}?${query}` : url.pathname;

  return {
    landingPage: landingPage.slice(0, URL_PATH_MAX),
    referrer: referrer.slice(0, URL_PATH_MAX),
    utm,
  };
}

@Injectable({ providedIn: 'root' })
export class LeadSourceService {
  /**
   * Captured when the service is constructed, which `APP_BASE_CONFIG` does at
   * boot, before any navigation moves the URL on. `undefined` on the server,
   * where no form is ever sent.
   */
  readonly value: LeadSource | undefined = isPlatformBrowser(
    inject(PLATFORM_ID),
  )
    ? readLeadSource(location.href, inject(DOCUMENT).referrer)
    : undefined;
}
